import type { FunctionalComponent } from 'preact';
import { route } from 'preact-router';
import { BUSINESS_INFO } from '@/data/business.ts';
import type { PageProps } from '@/types/props.ts';

/** 시안 `_business.html` 의 인라인 아이콘 3종 */
const ICONS = {
  address: (
    <path
      d="M12 5.33301C12 3.12634 10.2067 1.33301 8.00004 1.33301C5.79337 1.33301 4.00004 3.12634 4.00004 5.33301C4.00004 8.33301 8.00004 12.6663 8.00004 12.6663C8.00004 12.6663 12 8.33301 12 5.33301ZM6.66671 5.33301C6.66671 4.59967 7.26671 3.99967 8.00004 3.99967C8.73337 3.99967 9.33337 4.59967 9.33337 5.33301C9.33337 5.68663 9.1929 6.02577 8.94285 6.27582C8.6928 6.52586 8.35366 6.66634 8.00004 6.66634C7.64642 6.66634 7.30728 6.52586 7.05723 6.27582C6.80718 6.02577 6.66671 5.68663 6.66671 5.33301ZM3.33337 13.333V14.6663H12.6667V13.333H3.33337Z"
      fill="#777777"
    />
  ),
  email: (
    <path
      d="M13.3334 2.66699H2.66671C1.93337 2.66699 1.34004 3.26699 1.34004 4.00033L1.33337 12.0003C1.33337 12.7337 1.93337 13.3337 2.66671 13.3337H13.3334C14.0667 13.3337 14.6667 12.7337 14.6667 12.0003V4.00033C14.6667 3.26699 14.0667 2.66699 13.3334 2.66699ZM13.3334 5.33366L8.00004 8.66699L2.66671 5.33366V4.00033L8.00004 7.33366L13.3334 4.00033V5.33366Z"
      fill="#777777"
    />
  ),
  site: (
    <path
      d="M7.99337 1.33301C4.31337 1.33301 1.33337 4.31967 1.33337 7.99967C1.33337 11.6797 4.31337 14.6663 7.99337 14.6663C11.68 14.6663 14.6667 11.6797 14.6667 7.99967C14.6667 4.31967 11.68 1.33301 7.99337 1.33301ZM12.6134 5.33301H10.6467C10.4377 4.50741 10.1288 3.71041 9.72671 2.95967C10.9428 3.37834 11.9675 4.22082 12.6134 5.33301ZM8.00004 2.69301C8.55337 3.49301 8.98671 4.37967 9.27337 5.33301H6.72671C7.01337 4.37967 7.44671 3.49301 8.00004 2.69301ZM2.84004 9.33301C2.73337 8.90634 2.66671 8.45967 2.66671 7.99967C2.66671 7.53967 2.73337 7.09301 2.84004 6.66634H5.09337C5.04004 7.10634 5.00004 7.54634 5.00004 7.99967C5.00004 8.45301 5.04004 8.89301 5.09337 9.33301H2.84004ZM3.38671 10.6663H5.35337C5.56671 11.4997 5.87337 12.2997 6.27337 13.0397C5.05641 12.6226 4.0312 11.7797 3.38671 10.6663ZM5.35337 5.33301H3.38671C4.0312 4.21964 5.05641 3.37675 6.27337 2.95967C5.87129 3.71041 5.56234 4.50741 5.35337 5.33301ZM8.00004 13.3063C7.44671 12.5063 7.01337 11.6197 6.72671 10.6663H9.27337C8.98671 11.6197 8.55337 12.5063 8.00004 13.3063ZM9.56004 9.33301H6.44004C6.38004 8.89301 6.33337 8.45301 6.33337 7.99967C6.33337 7.54634 6.38004 7.09967 6.44004 6.66634H9.56004C9.62004 7.09967 9.66671 7.54634 9.66671 7.99967C9.66671 8.45301 9.62004 8.89301 9.56004 9.33301ZM9.72671 13.0397C10.1267 12.2997 10.4334 11.4997 10.6467 10.6663H12.6134C11.9675 11.7785 10.9428 12.621 9.72671 13.0397ZM10.9067 9.33301C10.96 8.89301 11 8.45301 11 7.99967C11 7.54634 10.96 7.10634 10.9067 6.66634H13.16C13.2667 7.09301 13.3334 7.53967 13.3334 7.99967C13.3334 8.45967 13.2667 8.90634 13.16 9.33301H10.9067Z"
      fill="#777777"
    />
  ),
};

const BizIcon: FunctionalComponent<{ kind: keyof typeof ICONS }> = ({ kind }) => (
  <span class="popup-biz-icon">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      {ICONS[kind]}
    </svg>
  </span>
);

/**
 * 회사 소개 — 사업자 정보 (시안 `_business.html` 의 `#bizPopup`).
 *
 * 시안은 푸터 브랜드를 누르면 열리는 팝업이고, 파일 자체는 팝업만
 * `display:block` 으로 열어 둔 형태다. 여기서는 푸터 링크(`/business`)가
 * 그대로 이 화면으로 온다.
 */
const Business: FunctionalComponent<PageProps> = () => {
  function close() {
    if (window.history.length > 1) window.history.back();
    else route('/', true);
  }

  return (
    <div class="page">
      <div class="common-popup-overlay is-open">
        <div class="common-popup">
          <div class="popup-header">
            <button type="button" class="popup-close" aria-label="닫기" onClick={close}>
              <img src="/images/close.png" alt="" />
            </button>
          </div>

          <div class="popup-body">
            <span class="popup-biz-title">
              <img src="/images/logo_b.png" alt="urnplay" />
            </span>

            <div class="popup-biz">
              <p class="popup-biz-name">{BUSINESS_INFO.name}</p>
              <p class="popup-biz-num">사업자번호 : {BUSINESS_INFO.registrationNumber}</p>

              <div class="popup-biz-detail">
                <div class="popup-biz-row">
                  <BizIcon kind="address" />
                  <span>{BUSINESS_INFO.address}</span>
                </div>
                <div class="popup-biz-row">
                  <BizIcon kind="email" />
                  <a href={`mailto:${BUSINESS_INFO.email}`}>{BUSINESS_INFO.email}</a>
                </div>
                <div class="popup-biz-row">
                  <BizIcon kind="site" />
                  <a href={BUSINESS_INFO.siteHref} target="_blank" rel="noreferrer">
                    {BUSINESS_INFO.siteLabel}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Business;
